import React from "react";
import PropTypes from "prop-types";
import { merge, get } from "lodash";
import MetricGroupItem from "./MetricGroupItem";
import { speakers } from "./index";

/* eslint quote-props:0 */
const LABELS = {
  "longest-streak": { label: "Longest Streak", unit: "seconds" },
  "median-streak": { label: "Median Streak", unit: "seconds" },
  "average-streak": { label: "Average Streak", unit: "seconds" },
  "longest-silence-streak": {
    label: "Longest Silence Streak",
    unit: "seconds",
  },
  "intra-call-change-in-pitch": { label: "Change in Pitch", unit: "score" },
  "intra-call-change-in-relative-voice-volume-energy": {
    label: "Change in Volume",
    unit: "score",
  },
  "intra-call-change-in-sentiment": {
    label: "Change in Sentiment",
    unit: "score",
  },
  "intra-call-change-in-talk-rate": {
    label: "Change in Talk Rate",
    unit: "score",
  },
  "voice-unpredictability": { label: "Voice Unpredictability", unit: "score" },
  vocabulary: { label: "Vocabulary", unit: "score" },
  "overtalk-incidents": { label: "Overtalk Incidents", unit: "incidents" },
  "overtalk-ratio": { label: "Overtalk Ratio", unit: "ratio" },
  "relative-voice-volume-energy": { label: "Volume/Energy", unit: "ratio" },
  "silence-incidents": { label: "Silence Incidents", unit: "incidents" },
  "silence-ratio": { label: "Silence Ratio", unit: "ratio" },
  "talk-rate": { label: "Talk Rate", unit: "score" },
  "talk-ratio": { label: "Talk Ratio", unit: "ratio" },
  "intra-agent-to-caller-talk-rate-ratio": {
    label: "Talk Rate Ratio",
    unit: "ratio",
  },
  "voice-dynamism-std-dev-score": { label: "Voice Dynamism", unit: "score" },
  overtalk: { label: "Overtalk", unit: "incidents" },
  "call-sentiment": { label: "Overall Sentiment", unit: "score" },
  sentiment: { label: "Overall Sentiment", unit: "score" },
  "start-sentiment": { label: "Start Sentiment", unit: "score" },
  "end-sentiment": { label: "End Sentiment", unit: "score" },
  "talk-style-tone-and-volume": { label: "Tone", unit: "score" },
  "talk-time-and-rate": { label: "Talk Time", unit: "score" },
  "messaging-response-time": {
    label: "Messaging Response Time",
    unit: "score",
  },
  "first-response-time": {
    label: "First Response Time",
    unit: "seconds",
  },
  "first-time-in-queue": {
    label: "First Time In Queue",
    unit: "seconds",
  },
  "active-duration": {
    label: "Active Duration",
    unit: "seconds",
  },
  "active-agent-duration": {
    label: "Active  Agent Duration",
    unit: "seconds",
  },
  "post-assign-first-response-time": {
    label: "Agent First Response Time (After Assigned)",
    unit: "seconds",
  },
  "time-to-assign": {
    label: "Time to Assign",
    unit: "seconds",
  },
  "first-time-in-bot": {
    label: "First Time In Bot",
    unit: "seconds",
  },
  "total-response-time": {
    label: "Total Response Time",
    unit: "seconds",
  },
  "avg-response-time": { label: "Average Response Time", unit: "seconds" },
  "max-response-time": { label: "Max Response Time", unit: "seconds" },
  "median-response-time": {
    label: "Median Response Time",
    unit: "seconds",
  },
  "total-wait-time": { label: "Total Wait Time", unit: "seconds" },
  "number-of-transitions-different-agents": {
    label: "Transfer Count",
    unit: "",
  },
  "message-count": { label: "Message Count", unit: "" },
  "responses-count": { label: "Response Count", unit: "" },
  mcs: { label: "MCS", unit: "" },
  "call-mcs": { label: "MCS", unit: "" },
};

const ORDER = [
  // "overtalk" Section
  "overtalk-incidents",
  "overtalk-ratio",

  // "sentiment" Section
  "intra-call-change-in-sentiment",
  "sentiment",
  "call-sentiment",
  "start-sentiment",
  "end-sentiment",

  // "talk-time-and-rate" Section
  "longest-streak",
  "median-streak",
  "average-streak",
  "longest-silence-streak",
  "intra-call-change-in-talk-rate",
  "silence-incidents",
  "silence-ratio",
  "talk-rate",
  "talk-ratio",
  "intra-agent-to-caller-talk-rate-ratio",

  // "MCS" Section
  "mcs",

  // "talk-style-tone-and-volume" Section
  "intra-call-change-in-pitch",
  "intra-call-change-in-relative-voice-volume-energy",
  "relative-voice-volume-energy",
  "voice-dynamism-std-dev-score",
  "vocabulary",
  "voice-unpredictability",

  // "messaging-response-time" Section
  "first-time-in-queue",
  "first-time-in-bot",
  "time-to-assign",
  "post-assign-first-response-time",
  "first-response-time",
  "active-agent-duration",
  "active-duration",
  "total-response-time",
  "avg-response-time",
  "max-response-time",
  "median-response-time",
  "total-wait-time",
  "number-of-transitions-different-agents",
  "message-count",
  "responses-count",
];

const StyledRow = ({ children, ...rest }) => (
  <div className="metrics-group-row" {...rest}>
    {children}
  </div>
);

const StyledBar = ({ children, ...rest }) => (
  <div className="metrics-group-bar" {...rest}>
    {children}
  </div>
);

/* eslint no-prototype-builtins:0 */
class MetricGroup extends React.Component {
  constructor(props) {
    super(props);
    this.data = {};
    this.sortedData = {};
    this.groupUnits = this.parseMetricGroupUnits(props.groupUnits);
  }

  componentWillMount() {
    const { metricValues } = this.props;
    this.sortedData = this.sortData(metricValues);
  }

  getLabel(val) {
    return get(LABELS, [val, "label"], "");
  }

  getUnits(val) {
    return get(this.groupUnits, [val, "unit"], get(LABELS, [val, "unit"], ""));
  }

  parseMetricGroupUnits(groupUnits) {
    const unitsResponse = {};
    groupUnits.forEach((val) => {
      val.metrics.forEach((metric) => {
        const isCallMetric = metric.metricName.startsWith("call-");
        const metricUnitKey = isCallMetric
          ? this.parseMetricUnit(
              metric.metricName.substring(metric.metricName.indexOf("-") + 1)
            )
          : this.parseMetricUnit(metric.metricName);
        unitsResponse[metricUnitKey] = {
          unit: metric.metricUnit !== "units" ? metric.metricUnit : "",
        };
      });
    });
    return unitsResponse;
  }

  // Gets the speaker associated with the specific metricName
  // ie. agent-first-response-time returns "Agent"
  // ie. bot-max-response-time returns "Bot"
  parseSpeaker(metricName) {
    const firstDashIndex = metricName.indexOf("-");
    const firstWordSubString = metricName.substr(0, firstDashIndex) || "";
    return speakers[firstWordSubString.toUpperCase()] || speakers.OVERALL;
  }

  // Gets the metric name after remove speaker ('agent' or 'caller') from metricName
  parseMetricLabel(metricName) {
    let nameSubstr = metricName;
    const firstDashIndex = metricName.indexOf("-");
    const firstWordSubString = metricName.substr(0, firstDashIndex) || "";

    if (speakers[firstWordSubString.toUpperCase()]) {
      nameSubstr = metricName.substr(firstDashIndex + 1);
    }
    return Object.keys(LABELS).find((key) => nameSubstr === key);
  }

  parseMetricUnit(metricName) {
    let nameSubstr = metricName;
    const firstDashIndex = metricName.indexOf("-");
    const firstWordSubString = metricName.substr(0, firstDashIndex) || "";

    if (speakers[firstWordSubString.toUpperCase()]) {
      nameSubstr = metricName.substr(firstDashIndex + 1);
    }
    return nameSubstr;
  }

  applySpeakerValues(speaker, metricLabel, metricValue) {
    const speakerValues = {};

    speakerValues[speaker.toLowerCase()] = {
      label: this.getLabel(metricLabel),
      units:
        this.getUnits(metricLabel) === "milliseconds"
          ? "seconds"
          : this.getUnits(metricLabel),
      val:
        this.getUnits(metricLabel) === "milliseconds"
          ? metricValue / 1000
          : metricValue,
      type: "aType",
    };

    return speakerValues;
  }

  /* eslint class-methods-use-this:0 */
  sortData(metricValues) {
    return metricValues.reduce((accValues, { metricName, metricValue }) => {
      const speakerName = this.parseSpeaker(metricName);

      const isCallMetric = metricName.startsWith("call-");
      const metricLabel = isCallMetric
        ? this.parseMetricLabel(
            metricName.substring(metricName.indexOf("-") + 1)
          )
        : this.parseMetricLabel(metricName);

      const speakerValues = this.applySpeakerValues(
        speakerName,
        metricLabel,
        metricValue
      );

      const formattedData = {
        [metricLabel]: {
          speaker: speakerValues,
          label: this.getLabel(metricLabel),
          units:
            this.getUnits(metricLabel) === "milliseconds"
              ? "seconds"
              : this.getUnits(metricLabel),
        },
      };

      merge(accValues, formattedData);
      return accValues;
    }, {});
  }

  render() {
    const { metricGroupName, filteredSpeakers } = this.props;

    return (
      <div>
        <StyledRow>
          <div>{this.getLabel(metricGroupName)}</div>
          {filteredSpeakers.map((speaker) => (
            <StyledBar />
          ))}
        </StyledRow>
        {ORDER.map((key) => {
          if (this.sortedData.hasOwnProperty(key) && this.sortedData[key]) {
            return (
              <MetricGroupItem
                key={key}
                data={this.sortedData[key]}
                filteredSpeakers={filteredSpeakers}
              />
            );
          } else {
            return null;
          }
        })}
      </div>
    );
  }
}

export default MetricGroup;

MetricGroup.propTypes = {
  metricGroupName: PropTypes.string.isRequired,
  metricValues: PropTypes.arrayOf(
    PropTypes.shape({
      metricName: PropTypes.string,
      metricValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
};
